import { Grid } from '@mui/material';
import { Checkbox, HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { HeaderContent } from '../../../components/HeaderContent';
import { UnderLicenseClaim } from '../../../components/new-layout/UnderLicenseClaim';
import { PLAN_TYPE } from '../../../constants/Subscriptions';
import { Project } from '../../../domain/project/Project';
import { ProjectDriversConfiguration } from '../../../domain/project/projectDrivers/ProjectDriversConfiguration';
import { intl } from '../../../helpers/IntGlobalProvider';
import { useRetrieveOrganization } from '../../../hooks/organization/useRetrieveOrganization';
import { useUpdateProject } from '../../../hooks/project/useUpdateProject';
import { ProjectDriversMapper } from '../../../mappers/project/ProjectDriversMapper';
import Text from '../../../react-components/Text';

const ConfigurationPill = ({
  field,
  enabled,
  onToggle,
}: {
  field: ProjectDriversConfiguration;
  enabled: boolean;
  onToggle: () => void;
}) => {
  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        maxWidth: `760px`,
        padding: `16px`,
        borderRadius: `10px`,
        border: `1px solid ${RoutalPalette.neutral10}`,
      }}
    >
      <Grid container direction="row" wrap="nowrap" alignItems="center">
        <Checkbox checked={enabled} onChange={onToggle} />
        <HSpacer small />
        <Text variant="body-large" intlId={`drivers.app.configuration.${field}`} />
      </Grid>
      <VSpacer small />
      <Text variant="label" intlId={`drivers.app.configuration.${field}.description`} />
    </Grid>
  );
};

interface DriversConfigurationProps {
  project: Project;
}

export const DriversConfiguration = ({ project }: DriversConfigurationProps) => {
  // const { data: user } = useRetrieveUser();
  // const { retrieveDoc } = useRetrieveSupportDocs();
  const { run: editProject } = useUpdateProject({ project });
  const { data: organization } = useRetrieveOrganization();

  if (organization?.subscription?.plan === PLAN_TYPE.MONKEY) {
    return (
      <UnderLicenseClaim headerText={intl.formatMessage({ id: `drivers.pod.under_gorilla_claim.title` })}>
        <Grid container direction="column" alignItems="center">
          <VSpacer large />
          <VSpacer large />
          <VSpacer large />
          <VSpacer large />
          <VSpacer medium />

          <Grid
            item
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            style={{ maxWidth: `700px` }}
          >
            <VSpacer large />
            <Text
              style={{ textAlign: `center`, color: `#5073B5` }}
              variant="h6"
              intlId="drivers.pod.under_gorilla_claim.header"
            />
          </Grid>
        </Grid>
      </UnderLicenseClaim>
    );
  }

  const toggleConfigurationField = (field: ProjectDriversConfiguration) => {
    const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

    projectDrivers.configuration = projectDrivers.configuration.includes(field)
      ? project.drivers.configuration.filter((c) => c !== field)
      : [...project.drivers.configuration, field];

    editProject({
      drivers: ProjectDriversMapper.toDomain({
        ...projectDrivers,
        stop_fields: projectDrivers.stop_fields,
      }),
    });
  };

  return (
    <Grid container direction="column" wrap="nowrap" style={{ height: `100%` }}>
      <HeaderContent
        titleIntlId="drivers.app.configuration.title"
        // subtitle="drivers.app.configuration.subtitle"
        // article={{
        //   intlId: `drivers.app.configuration.article`,
        //   link: retrieveDoc({ supportPage: `communicationCustomers`, language: user?.language.value }),
        // }}
        // video={{
        //   intlId: `watch_video`,
        //   link: `https://www.youtube.com/watch?v=ZdZVx0ahpEI&t=137s`,
        // }}
        styles={{
          container: {
            padding: `16px 22px`,
            backgroundColor: RoutalPalette.white,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            maxHeight: `120px`,
          },
        }}
      />

      <Grid
        container
        direction="column"
        wrap="nowrap"
        style={{
          padding: `20px 20px 0px 20px`,
          height: `calc(100% - 117px)`,
          backgroundColor: RoutalPalette.neutral00,
          overflowY: `auto`,
        }}
      >
        {([`stop_duration`, `pin`] as ProjectDriversConfiguration[]).map((field, index, array) => (
          <>
            <ConfigurationPill
              field={field}
              enabled={project.drivers.configuration.includes(field)}
              onToggle={() => toggleConfigurationField(field)}
            />
            {index !== array.length - 1 && <VSpacer large />}
          </>
        ))}
        <VSpacer large />
        {([`driver_position`, `pictures_gallery`] as ProjectDriversConfiguration[]).map((field, index, array) => (
          <>
            <ConfigurationPill
              field={field}
              enabled={project.drivers.configuration.includes(field)}
              onToggle={() => toggleConfigurationField(field)}
            />
            {index !== array.length - 1 && <VSpacer large />}
          </>
        ))}
        <VSpacer large />
        {([`survey`] as ProjectDriversConfiguration[]).map((field, index, array) => (
          <>
            <ConfigurationPill
              field={field}
              enabled={project.drivers.configuration.includes(field)}
              onToggle={() => toggleConfigurationField(field)}
            />
            {index !== array.length - 1 && <VSpacer large />}
          </>
        ))}
      </Grid>
    </Grid>
  );
};
